import { useState,useEffect,useMemo } from "react"
import { useQuery,useMutation,useQueryClient } from "@tanstack/react-query"
import { isAxiosError } from '@/lib/api-client'
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { toast } from "sonner"

import { Skeleton } from "@/components/ui/skeleton"
import { Form } from "@/components/ui/form"
import { Card,CardHeader } from "@/components/ui/card"
import { queryKeys } from "@/lib/query-keys"
import {
getPricingSchemes,
getCategoryUoms,
getAllUoms,
getProduct,
getSaleConfig,
createSaleConfig,
updateSaleConfig,
deleteSaleConfig,
} from "@/features/catalog/api"
import type { Product,SaleConfig,Shop,SaleConfigPayload } from "@/types"
import { PricingSection } from "./pricing-section"
import { SellMechanicsSection } from "./sell-mechanics-section"
import { PurchasingLimitsCard,TargetShopCard } from "./purchasing-limits-card"
import { BehaviorFlagsCard,FormActionBar } from "./behavior-flags-card"
import {
formSchema,
getDefaultValues,
type SaleConfigFormValues,
} from "./sale-config-utils"

interface SaleConfigFormProps {
  productId: string
  categoryId?: string
  shopId: string
  shops: Shop[]
  saleConfigId?: string | null
  onSuccess: (createdConfigId?: string) => void
  onCancel: () => void
  onShopSelectionChange?: (shopIds: string[]) => void
}

export const SaleConfigForm = ({ productId, categoryId, shopId, shops, saleConfigId, onSuccess, onCancel, onShopSelectionChange }: SaleConfigFormProps) => {
  const queryClient = useQueryClient()
  
  const [showPurchaseLimits, setShowPurchaseLimits] = useState(false)
  const [targetShopIds, setTargetShopIds] = useState<string[]>(saleConfigId ? (shopId ? [shopId] : []) : shops.map(s => s.id))
  // Which config the selection above was seeded from. Seeding is done once per
  // config (adjust-during-render) — re-seeding on every refetch would wipe a
  // shop the user just picked before they could save it.
  const [seededFor, setSeededFor] = useState<string | null>(null)
  
  const [pricingMode, setPricingMode] = useState<'simple' | 'tiered'>('simple')
  const [simplePriceDisplay, setSimplePriceDisplay] = useState('')

  useEffect(() => {
    if (onShopSelectionChange) {
      onShopSelectionChange(targetShopIds)
    }
  }, [targetShopIds, onShopSelectionChange])

  const schemesQuery = useQuery({
    queryKey: queryKeys.pricingSchemes(),
    queryFn: getPricingSchemes,
  })

  const uomsQuery = useQuery({
    queryKey: queryKeys.uomsForCategory(categoryId ?? null),
    queryFn: () => categoryId ? getCategoryUoms(categoryId) : getAllUoms(),
  })

  const allUomsQuery = useQuery({
    queryKey: queryKeys.allUoms(),
    queryFn: getAllUoms,
    enabled: !!categoryId,
  })

  const productQuery = useQuery({
    queryKey: queryKeys.product(productId),
    queryFn: () => getProduct(productId),
    enabled: !!productId,
  })

  const configQuery = useQuery({
    queryKey: queryKeys.saleConfig(saleConfigId || ''),
    queryFn: () => getSaleConfig(saleConfigId!),
    enabled: !!saleConfigId,
  })

  const initialLoading = schemesQuery.isLoading || uomsQuery.isLoading || productQuery.isLoading || (!!saleConfigId && configQuery.isLoading)

  const pricingSchemes = useMemo(() => schemesQuery.data || [], [schemesQuery.data])
  const uoms = useMemo(() => uomsQuery.data || [], [uomsQuery.data])
  const allWeightUoms = useMemo(
    () => allUomsQuery.data?.filter(u => u.measurementType === 'WEIGHT') || uoms.filter(u => u.measurementType === 'WEIGHT'),
    [allUomsQuery.data, uoms]
  )
  const saleConfigData = configQuery.data

  // Seed the shop selection from the config's real links the first time this
  // config loads. GET /sale-configs/{id} returns `shops[]`, not a flat shopId,
  // so without this the selector shows only the current shop and a second shop
  // can be neither seen nor removed.
  if (saleConfigId && saleConfigData && seededFor !== saleConfigId) {
    setSeededFor(saleConfigId)
    const linked = (saleConfigData.shops ?? [])
      .map((s) => s.shopId ?? s.shop?.id)
      .filter((id): id is string => !!id)
    setTargetShopIds(linked.length > 0 ? linked : [saleConfigData.shopId ?? shopId])
  }

  const form = useForm<SaleConfigFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(),
  })

  useEffect(() => {
    if (saleConfigData) {
      form.reset(getDefaultValues(saleConfigData))
      
      const price = saleConfigData.flatPriceCents ?? saleConfigData.simplePriceCents
      if (price !== null && price !== undefined) {
        setPricingMode('simple')
        setSimplePriceDisplay((price / 100).toFixed(2))
      } else if (saleConfigData.pricingSchemeId) {
        setPricingMode('tiered')
      }
    }
  }, [saleConfigData, form])

  const sellType = form.watch("sellType")
  const sellUomId = form.watch("sellUomId")
  const unitWeightUomId = form.watch("unitWeightUomId")
  const simplePriceCents = form.watch("simplePriceCents")
  const pricingSchemeId = form.watch("pricingSchemeId")

  const handleSellTypeChange = (newSellType: string) => {
    form.setValue("sellType", newSellType as "WEIGHT" | "COUNT" | "VOLUME")
    if (pricingMode === 'tiered') form.setValue("pricingSchemeId", "")
    if (newSellType === "WEIGHT" || newSellType === "VOLUME") {
      form.setValue("unitWeight", null)
      form.setValue("unitWeightUomId", null)
    }
  }

  const handlePricingModeChange = (newMode: 'simple' | 'tiered') => {
    setPricingMode(newMode)
    if (newMode === 'simple') {
      form.setValue("pricingSchemeId", undefined)
    } else {
      form.setValue("simplePriceCents", undefined)
      setSimplePriceDisplay('')
    }
  }

  const filteredSellUoms = useMemo(() => {
    return uoms.filter(u => {
      if ((pricingMode === 'tiered') || sellUomId) {
        if (u.measurementType !== sellType) return false
      }
      if (pricingMode === 'simple') return true
      if (pricingMode === 'tiered' && pricingSchemeId) {
        const scheme = pricingSchemes.find(s => s.id === pricingSchemeId)
        if (!scheme) return true
        if (scheme.weightUomId) return u.id === scheme.weightUomId
        const isWeightScheme = scheme.tieringMethod === "WEIGHT_BASED" || scheme.pricingMethod === "WEIGHT_TIER"
        if (isWeightScheme) return u.measurementType === "WEIGHT"
        const isCountScheme = scheme.tieringMethod === "QUANTITY_BASED" || scheme.pricingMethod === "UNIT_TIER"
        if (isCountScheme) return u.measurementType === "COUNT"
      }
      return true
    })
  }, [uoms, pricingMode, sellUomId, sellType, pricingSchemeId, pricingSchemes])

  const weightUoms = allWeightUoms.length > 0 ? allWeightUoms : uoms.filter(u => u.measurementType === "WEIGHT")

  const filteredPricingSchemes = useMemo(() => {
    return pricingSchemes.filter(s => {
      const hasWeightUnits = uoms.some(u => u.measurementType === 'WEIGHT')
      const hasCountUnits = uoms.some(u => u.measurementType === 'COUNT')

      const isWeightScheme = s.tieringMethod === "WEIGHT_BASED" || s.pricingMethod === "WEIGHT_TIER"
      const isCountScheme = s.tieringMethod === "QUANTITY_BASED" || s.pricingMethod === "UNIT_TIER"

      if (sellUomId && pricingMode !== 'tiered') {
        if (sellType === 'WEIGHT' && !isWeightScheme) return false
        if (sellType === 'COUNT' && !isCountScheme) return false
      }

      if (isWeightScheme && !hasWeightUnits) return false
      if (isCountScheme && !hasCountUnits) return false

      if (s.weightUomId) {
        const uomAvailable = uoms.some(u => u.id === s.weightUomId)
        if (!uomAvailable) return false
      }
      return true
    })
  }, [pricingSchemes, uoms, sellUomId, pricingMode, sellType])

  useEffect(() => {
    if (pricingMode === 'tiered' && pricingSchemeId) {
      const selectedScheme = pricingSchemes.find(s => s.id === pricingSchemeId)
      if (!selectedScheme) return

      if (selectedScheme.weightUomId) {
        if (sellUomId !== selectedScheme.weightUomId) {
          form.setValue("sellUomId", selectedScheme.weightUomId)
          const uom = uoms.find(u => u.id === selectedScheme.weightUomId)
          if (uom) form.setValue("sellType", uom.measurementType as "WEIGHT" | "COUNT" | "VOLUME")
        }
        return
      }

      const isWeightScheme = selectedScheme.tieringMethod === "WEIGHT_BASED" || selectedScheme.pricingMethod === "WEIGHT_TIER"
      const isCountScheme = selectedScheme.tieringMethod === "QUANTITY_BASED" || selectedScheme.pricingMethod === "UNIT_TIER"

      if (isWeightScheme && (sellType !== 'WEIGHT' || !sellUomId)) {
        form.setValue("sellType", "WEIGHT")
        const gramUnit = uoms.find(u => u.measurementType === 'WEIGHT' && (u.name.toLowerCase().includes('gram') || u.shortForm.toLowerCase() === 'g'))
        const firstWeight = uoms.find(u => u.measurementType === 'WEIGHT')
        const targetUom = gramUnit || firstWeight
        if (targetUom && sellUomId !== targetUom.id) {
          form.setValue("sellUomId", targetUom.id)
        }
      } else if (isCountScheme && (sellType !== 'COUNT' || !sellUomId)) {
        form.setValue("sellType", "COUNT")
        const firstCount = uoms.find(u => u.measurementType === 'COUNT')
        if (firstCount && sellUomId !== firstCount.id) {
          form.setValue("sellUomId", firstCount.id)
        }
      }
    }
  }, [pricingMode, pricingSchemeId, form, uoms, pricingSchemes, sellType, sellUomId])

  useEffect(() => {
    if (pricingMode !== 'simple') return
    if (sellUomId) return
    if (filteredSellUoms.length === 0) return

    const firstUom = filteredSellUoms[0]
    form.setValue("sellUomId", firstUom.id)
    form.setValue("sellType", firstUom.measurementType as "WEIGHT" | "COUNT" | "VOLUME")
  }, [pricingMode, filteredSellUoms, sellUomId, form])

  useEffect(() => {
    if (sellType === "COUNT" && !saleConfigId && allWeightUoms.length > 0) {
      // If already set to something valid, don't overwrite
      if (unitWeightUomId && allWeightUoms.some(u => u.id === unitWeightUomId)) return

      const gramUnit = allWeightUoms.find(u => {
        const name = u.name.toLowerCase()
        const short = u.shortForm.toLowerCase()
        return (name === 'gram' || name === 'grams' || short === 'g')
      })
      
      if (gramUnit) {
        form.setValue("unitWeightUomId", gramUnit.id)
      } else {
        form.setValue("unitWeightUomId", allWeightUoms[0].id)
      }
    }
  }, [sellType, saleConfigId, allWeightUoms, form, unitWeightUomId])

  const mutation = useMutation({
    mutationFn: async (values: SaleConfigFormValues) => {
      if (targetShopIds.length === 0) throw new Error("Please select at least one shop.")
      if (pricingMode === 'simple' && (!values.simplePriceCents || values.simplePriceCents <= 0)) {
        throw new Error("Price is required and must be greater than 0")
      }
      if (pricingMode === 'tiered' && !values.pricingSchemeId) {
        throw new Error("Please select a pricing scheme")
      }

      if (pricingMode === 'tiered') {
        const selectedScheme = pricingSchemes.find(s => s.id === values.pricingSchemeId)
        if (selectedScheme) {
          const isWeightSale = values.sellType === "WEIGHT"
          const isWeightScheme = selectedScheme.tieringMethod === "WEIGHT_BASED" || selectedScheme.pricingMethod === "WEIGHT_TIER"
          const isQuantityScheme = selectedScheme.tieringMethod === "QUANTITY_BASED" || selectedScheme.pricingMethod === "UNIT_TIER"

          if (isWeightSale && isQuantityScheme) {
            throw new Error("You cannot use a Quantity-Based pricing scheme for a Weight-based sale.")
          }
          if (!isWeightSale && isWeightScheme) {
            throw new Error("You cannot use a Weight-Based pricing scheme for a Count-based sale.")
          }
        }
      }

      const internalName = saleConfigData?.name || `config_${Math.random().toString(36).substring(2, 9)}`

      const payload: SaleConfigPayload = {
        productId,
        name: internalName,
        sellType: values.sellType,
        sellUomId: values.sellUomId,
        minQuantity: values.minQuantity,
        maxQuantity: values.maxQuantity || null,
        quantityStep: values.quantityStep,
        defaultQuantity: values.defaultQuantity || null,
        unitWeight: values.unitWeight || null,
        unitWeightUomId: values.unitWeightUomId || null,
        isActive: values.isActive,
        allowOnlineSale: values.allowOnlineSale,
        isDiscountable: values.isDiscountable,
        isDeliverable: values.isDeliverable,
        isSample: values.isSample,
        isPromo: values.isPromo,
      }

      if (pricingMode === 'simple') {
        payload.simplePriceCents = values.simplePriceCents
      } else {
        payload.pricingSchemeId = values.pricingSchemeId
      }

      if (saleConfigId) {
        // One config can serve several shops, so the selection is saved as the
        // config's shop links — that is what makes adding *and* removing a shop
        // work, including moving it off the shop it was opened from. The API
        // requires at least one, which the selector already enforces.
        if (targetShopIds.length === 0) throw new Error('Select at least one shop for this configuration.')
        return await updateSaleConfig(saleConfigId, { ...payload, shopIds: targetShopIds })
      } else {
        return await createSaleConfig({ ...payload, shopIds: targetShopIds })
      }
    },
    onSuccess: (data: SaleConfig) => {
      if (saleConfigId) {
        const otherShops = targetShopIds.filter((id) => id !== shopId).length
        toast.success(
          otherShops > 0
            ? `Configuration updated · also available in ${otherShops} other shop${otherShops > 1 ? 's' : ''}`
            : "Configuration updated",
        )
        onSuccess()
      } else {
        toast.success(`Configuration${targetShopIds.length > 1 ? 's' : ''} created`)
        onSuccess(data?.id)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.saleConfigs(productId) })
    },
    onError: (error: unknown) => {
      if (error instanceof Error && error.message.includes("select a pricing scheme")) {
        form.setError("pricingSchemeId", { type: "manual", message: error.message })
        return
      }
      if (error instanceof Error && error.message.includes("Price is required")) {
        form.setError("simplePriceCents", { type: "manual", message: error.message })
        return
      }
      if (error instanceof Error && error.message.includes("You cannot use")) {
        toast.error(error.message)
        return
      }

      const msg = isAxiosError(error) ? error.response?.data?.message : (error instanceof Error ? error.message : "Something went wrong")
      const messages = Array.isArray(msg) ? msg : [msg]
      
      let hasGlobalError = false
      messages.forEach((m: string) => {
        const lowerMsg = m.toLowerCase()
        if (lowerMsg.includes("sell unit") || lowerMsg.includes("uom")) form.setError("sellUomId", { type: "server", message: m })
        else if (lowerMsg.includes("price")) form.setError("simplePriceCents", { type: "server", message: m })
        else if (lowerMsg.includes("pricing") || lowerMsg.includes("scheme")) form.setError("pricingSchemeId", { type: "server", message: m })
        else if (lowerMsg.includes("min")) form.setError("minQuantity", { type: "server", message: m })
        else if (lowerMsg.includes("max")) form.setError("maxQuantity", { type: "server", message: m })
        else hasGlobalError = true
      })

      if (hasGlobalError || messages.length === 0) {
        toast.error(messages.join(", "))
      } else {
        toast.error("Please check the highlighted fields.")
      }
    }
  })

  type ProductsListCache = { data: Product[]; meta?: unknown }

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (saleConfigId) {
        await deleteSaleConfig(saleConfigId)
      }
    },
    onMutate: async () => {
      if (!saleConfigId) return undefined
      // Stop any in-flight product fetches from overwriting the optimistic removal.
      await queryClient.cancelQueries({ queryKey: queryKeys.products(shopId) })
      await queryClient.cancelQueries({ queryKey: queryKeys.productPrefix() })

      // Snapshot every affected cache entry so onError can restore them exactly.
      const prevLists = queryClient.getQueriesData<ProductsListCache>({ queryKey: queryKeys.products(shopId) })
      const prevProducts = queryClient.getQueriesData<Product>({ queryKey: queryKeys.productPrefix() })

      const stripConfig = (configs?: SaleConfig[]): SaleConfig[] =>
        (configs ?? []).filter((c) => c.id !== saleConfigId)

      // Remove the config from every paginated/searched products-list cache variant.
      queryClient.setQueriesData<ProductsListCache>({ queryKey: queryKeys.products(shopId) }, (old) => {
        if (!old?.data) return old
        return {
          ...old,
          data: old.data.map((p) =>
            p.id === productId ? { ...p, saleConfigs: stripConfig(p.saleConfigs) } : p,
          ),
        }
      })

      // Remove it from any single-product cache holding this product (keyed by id or slug).
      queryClient.setQueriesData<Product>({ queryKey: queryKeys.productPrefix() }, (old) => {
        if (!old || old.id !== productId) return old
        return { ...old, saleConfigs: stripConfig(old.saleConfigs) }
      })

      return { prevLists, prevProducts }
    },
    onSuccess: () => {
      toast.success("Sale configuration deleted")
      if (onSuccess) onSuccess()
    },
    onError: (_err, _vars, context) => {
      context?.prevLists?.forEach(([key, data]) => queryClient.setQueryData(key, data))
      context?.prevProducts?.forEach(([key, data]) => queryClient.setQueryData(key, data))
      toast.error("Failed to delete sale configuration")
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.saleConfigs(productId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.products(shopId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.productPrefix() })
    },
  })

  const onSubmit = (values: SaleConfigFormValues) => {
    mutation.mutate(values)
  }

  if (initialLoading) {
    return (
      <div className="space-y-6">
        <Card><CardHeader><Skeleton className="h-5 w-32" /></CardHeader></Card>
        <Card><CardHeader><Skeleton className="h-5 w-32" /></CardHeader></Card>
      </div>
    )
  }

  const onError = (errors: Record<string, { message?: string }>) => {
    const firstError = Object.keys(errors).reduce((field, a) => {
      return field || (errors[a] ? a : "")
    }, "")
    if (firstError) {
      const element = document.getElementsByName(firstError)[0]
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" })
        setTimeout(() => element.focus(), 100)
      }
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, onError)} className="space-y-4">
        <TargetShopCard
          shops={shops}
          saleConfigId={saleConfigId}
          targetShopIds={targetShopIds}
          onTargetShopIdsChange={setTargetShopIds}
          lockedShopId={saleConfigData?.shopId ?? shopId}
        />

        <PricingSection
          control={form.control}
          pricingMode={pricingMode}
          onPricingModeChange={handlePricingModeChange}
          simplePriceDisplay={simplePriceDisplay}
          onSimplePriceDisplayChange={setSimplePriceDisplay}
          filteredPricingSchemes={filteredPricingSchemes}
          uoms={uoms}
          formGetValues={form.getValues}
        />

        {((pricingMode === 'simple' && simplePriceCents && simplePriceCents > 0) || (pricingMode === 'tiered' && pricingSchemeId)) && (
          <div className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-500">
            <SellMechanicsSection
              control={form.control}
              pricingMode={pricingMode}
              filteredSellUoms={filteredSellUoms}
              allCategoryUoms={uoms}
              allWeightUoms={weightUoms}
              sellType={sellType}
              onSellTypeChange={handleSellTypeChange}
            />

            <PurchasingLimitsCard
              control={form.control}
              show={showPurchaseLimits}
              onToggleShow={() => setShowPurchaseLimits((prev) => !prev)}
            />

            <BehaviorFlagsCard control={form.control} />
          </div>
        )}

        <FormActionBar
          saleConfigId={saleConfigId}
          deletePending={deleteMutation.isPending}
          savePending={mutation.isPending}
          onDelete={() => deleteMutation.mutate()}
          onCancel={onCancel}
        />
      </form>
    </Form>
  )
}
